import 'style-loader!./oracle-text-condition.css';
import h from 'hyperscript';
import { createSlider } from 'ui/slider';
import { default as i18n, defineNamespace } from 'cadenza/utils/i18n';
import messages from './messages_de.properties';
import { default as OracleTextConditionApi } from 'cadenza/api-client/conditions/condition';
import ConditionElement from '../condition-element';
import logger from 'cadenza/utils/logging';
import { default as onEvent } from 'cadenza/utils/events';
import { throttle } from 'lodash';
import { disableElements } from 'cadenza/utils/dom';

defineNamespace('oracle-text-condition', messages);

const MIN_ACCURACY = 60;
const MAX_ACCURACY = 80;

const SLIDER_STEP = 2;

const INPUT_LISTENER_FREQUENCY = 300;

class OracleTextCondition extends ConditionElement {

  connectedCallback () {
    this._oracleTextExpression = h('.oracleTextExpression.form-control.with-disabled-overlay', {contentEditable: true});
    this._filterBtn = h('button.fakeButton', {
      type: 'button',
      onclick: (event) => {
        event.stopPropagation();
        this._activateCondition();
      }
    }, i18n('ok'));

    const sliderElement = h('div.oracleTextSlider', {
      'onkeydown': event => {
        this._changeFilterAccuracy(event, this.selectedScore);
        this._toggleOkButton();
      }
    });

    this._noUISlider = this._createSlider(sliderElement);
    this._noUISlider.on('change', () => this._toggleOkButton());

    this.append(this._oracleTextExpression, this._filterBtn, sliderElement);

    this.conditionApi = new OracleTextConditionApi(this.selectorId, this.conditionName);
    disableElements(!this.enabled, this._oracleTextExpression, this._filterBtn, sliderElement);
    this._setInitState(this.previousElementSibling.value);

    onEvent(this._oracleTextExpression, 'input textinput keyup', throttle(() => this._toggleOkButton(), INPUT_LISTENER_FREQUENCY).bind(this));
  }

  _setInitState (data) {
    this.oracleTextExpression = data.oracleTextExpression || '';
    this.selectedScore = data.selectedScore;
    this._lastOracleTextExpression = this.oracleTextExpression;
    this._lastOracleTextSelectedScore = this.selectedScore;
    disableElements(true, this._filterBtn);
  }

  _activateCondition () {
    this.conditionApi.setValue({
      selectedScore: this.selectedScore,
      oracleTextExpression: this.oracleTextExpression
    }).then(() => super.activateCondition()).catch(error => logger.error('Cannot filter due to: ', error));
  }

  _createSlider (sliderContainer) {
    return createSlider(sliderContainer, {
      range: {'min': MIN_ACCURACY, 'max': MAX_ACCURACY},
      start: MAX_ACCURACY,
      step: SLIDER_STEP,
      direction: 'rtl',
      pips: {
        mode: 'positions',
        values: [0, 100],
        density: 10,
        format: {
          to: val => val === MIN_ACCURACY ? i18n('oracle-text-condition:search.fuzzy') : i18n('oracle-text-condition:search.exact')
        }
      }
    });
  }

  _changeFilterAccuracy (event, currentScore) {
    if (event.key === 'ArrowLeft' || event.key === 'Left') {
      event.stopPropagation();
      if (currentScore < MAX_ACCURACY) {
        this.selectedScore = currentScore + SLIDER_STEP;
      }
    } else if (event.key === 'ArrowRight' || event.key === 'Right') {
      event.stopPropagation();
      if (currentScore > MIN_ACCURACY) {
        this.selectedScore = currentScore - SLIDER_STEP;
      }
    }
  }

  set selectedScore (selectedScore) {
    if (selectedScore) {
      this._noUISlider.set(selectedScore);
    }
  }

  get selectedScore () {
    return Number(this._noUISlider.get());
  }

  set oracleTextExpression (oracleTextExpression) {
    this._oracleTextExpression.textContent = oracleTextExpression;
  }

  get oracleTextExpression () {
    return this._oracleTextExpression.textContent;
  }

  _toggleOkButton () {
    if (!this.oracleTextExpression || (this._isSameOracleTextScore() && this._isSameOracleTextExpression())) {
      disableElements(true, this._filterBtn);
    } else {
      disableElements(false, this._filterBtn);
    }
  }

  _isSameOracleTextExpression () {
    const currentOracleTextExpression = this.oracleTextExpression;
    return currentOracleTextExpression && this._lastOracleTextExpression === currentOracleTextExpression;
  }

  _isSameOracleTextScore () {
    const currentOracleTextScore = this.selectedScore;
    return currentOracleTextScore && this._lastOracleTextSelectedScore === currentOracleTextScore;
  }

}

customElements.define('d-oracle-text-condition', OracleTextCondition);
